<template>
  <section
    class="w-full rounded-[8px] border box-border flex justify-between gap-0 font-['Visuelt_Pro',sans-serif]"
    :class="isExpanded
      ? 'bg-[linear-gradient(-68.44deg,_#6a2f29,_#a86361_50%,_#6c69ad)] border-[#eaeaea] text-white items-start p-4'
      : 'bg-white border-[#a86361] text-[#6A2F29] items-center px-4 py-2 gap-6'"
    role="region"
    aria-label="Taia guided results"
  >
   <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {{ loadingAnnouncement }}
    </div>
    <div :id="contentId" class="flex-1 flex flex-col items-start justify-center gap-2">
      <div class="flex items-center gap-2">
        <img
          :src="isExpanded ? taiaAgenticExpandedIcon : taiaAgenticCollapsedIcon"
          alt=""
          aria-hidden="true"
          class="w-4 h-4 block object-contain"
        >
        <p class="m-0 text-[14px] md:text-[16px] leading-[1]">
          {{ isExpanded ? 'Taia guided results' : collapsedCopy }}
        </p>
      </div>

      <template v-if="isExpanded">
        <div class="flex flex-col gap-1">
          <h2 class="m-0 text-[24px] md:text-[32px] leading-[1.1] tracking-[-0.5px] font-normal">{{ taiaResultsHeadline }}</h2>
          <p class="m-0 text-[16px] leading-[1.4]">{{ shortDescription }}</p>
        </div>

        <div class="flex items-center flex-wrap gap-2 pt-4" role="group" aria-label="Focus area options">
          <button
            v-for="(focusArea, index) in focusAreas"
            :key="focusArea.filter_name"
            type="button"
            class="cursor-pointer rounded-[4px] border border-white/50 bg-transparent text-white inline-flex items-center px-3 py-2 text-[14px] leading-[1.4] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            :aria-label="`Focus area ${focusArea.display_name}, ${index + 1} of ${focusAreas.length}`"
            :aria-pressed="selectedFocusArea === focusArea.filter_name"
            @click="handleFocusAreaSelect(focusArea.filter_name)"
          >
            {{ focusArea.display_name }}
          </button>
        </div>
      </template>
    </div>

    <button
      type="button"
      class="w-6 h-6 border-0 bg-transparent p-0 cursor-pointer text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
      :aria-expanded="isExpanded"
      :aria-controls="contentId"
      :aria-label="isExpanded ? 'Minimize Taia guided results' : 'Maximize Taia guided results'"
      @click="toggleBanner"
    >
      <span class="sr-only">{{ isExpanded ? 'Collapse section' : 'Expand section' }}</span>
      <Icon :icon="isExpanded ? 'minus' : 'plus'" aria-hidden="true" />
    </button>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import Icon from '~/components/ui/atom/icon.vue'
import eventBusGtm from '~/composables/eventBusGtm'
import { useUserStore } from '~/stores/userStore'
import { useIframeStore } from '~/stores/chatAIStore'
import { storeToRefs } from 'pinia'

import taiaAgenticExpandedIcon from '~/assets/tgpt/images/taia-agentic-white.svg'
import taiaAgenticCollapsedIcon from '~/assets/tgpt/images/taia-agentic-red.svg'

type FocusArea = {
  filter_name: string
  display_name: string
}

const props = withDefaults(
  defineProps<{
    title?: string
    shortDescription?: string
    initialExpanded?: boolean
    collapsedCopy?: string
    focusAreas?: FocusArea[]
  }>(),
  {
    initialExpanded: true,
    collapsedCopy: 'Choose a focus area to narrow your results',
    focusAreas: () => [
      { filter_name: 'build_muscle', display_name: 'Build Muscle' },
      { filter_name: 'recovery', display_name: 'Recovery' },
      { filter_name: 'endurance', display_name: 'Endurance' },
      { filter_name: 'energy', display_name: 'Energy' }
    ]
  }
)

const emit = defineEmits<{
  selectFocusArea: [filterName: string]
}>()

const isExpanded = ref(props.initialExpanded)
const selectedFocusArea = ref<string | null>(null)
const contentId = 'taia-agentic-banner-content'
const userStore = useUserStore()
const iframeStore = useIframeStore()
const { isDynamicYieldVariationData } = storeToRefs(iframeStore)
const loadingAnnouncement = ref('')
const loadingMessage = 'We’re grabbing your products. Hold on a few seconds while we retrieve your products.'
const collapsedCopy = computed(() => props.collapsedCopy)

const trackTaiaBannerEvent = (buttonName: string, filterName: string | null = null) => {
  const customerId = userStore.data?.customerId ?? null
  eventBusGtm.emit('trackEvent', {
    event: 'Agenticplp',
    button_name: buttonName,
    screen_name: 'PLP',
    Customer_id: customerId,
    filter_name: filterName,
    sub_screen: 'Agenticplp_banner',
    variationName: isDynamicYieldVariationData.value?.name ?? null,
    experienceName: isDynamicYieldVariationData.value?.experienceName ?? null
  })
}

const handleFocusAreaSelect = (filterName: string) => {
  if (selectedFocusArea.value === filterName) return
  selectedFocusArea.value = filterName
  loadingAnnouncement.value = ''
  nextTick(() => {
    loadingAnnouncement.value = loadingMessage
  })
  trackTaiaBannerEvent('click_filter_name', filterName)
  emit('selectFocusArea', filterName)
}

const toggleBanner = () => {
  const nextState = !isExpanded.value
  isExpanded.value = nextState
  trackTaiaBannerEvent(nextState ? 'maximize' : 'minimize')
}

const taiaResultsHeadline = computed(() =>
  props.title
)
</script>
